import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator } from "@frui.ts/validation";
import { computed, observable, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import OptionsService from "services/optionsService";
import { Modules, newNavigationName } from "config";
import SystemComponent, { SystemComponentValidationRules } from "entities/systemComponent";
import SystemComponentState from "entities/systemComponentState";
import EnumRepository from "repositories/enumRepository";
import CreateViewModel from "../../createViewModel";
import ManualEntityValidator from "@frui.ts/validation/dist/manualEntityValidator";
import { bound } from "@frui.ts/helpers";

@Router.registerRoute({ name: Router.Self, route: newNavigationName })
export default class SystemComponentCreateViewModel extends CreateViewModel<SystemComponent> {
  readonly modelName = "systemComponent";
  @observable private alreadyUsedCodes: string[] = [];
  private unsubscriptions: Array<() => void> = [];
  private manualValidator = new ManualEntityValidator<SystemComponent>(true);

  @computed
  get stateOptions() {
    return this.optionsService.internalEnumOptions(SystemComponentState);
  }

  constructor(
    protected repository: EnumRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    private optionsService: OptionsService
  ) {
    super();
    this.navigationName = newNavigationName;
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.unsubscriptions.push(reaction(() => this.item.code, this.onCodeChange));
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    this.alreadyUsedCodes = [];
    return super.onDeactivate(isClosing);
  }

  protected loadDetail() {
    const entity = new SystemComponent();
    attachAutomaticValidator(entity, {
      ...SystemComponentValidationRules,
      code: { ...SystemComponentValidationRules.code, manualValidation: this.manualValidator },
    });
    return entity;
  }

  protected async postData() {
    try {
      return await this.repository.create(this.item, "system_components");
    } catch (e: any) {
      if (e.response.status === 400) {
        this.alreadyUsedCodes.push(this.item.code);
        this.addAlreadyExistCodeError();
      } else {
        this.notificationService.addUnexpectedError();
      }
      throw e;
    }
  }

  @bound
  private onCodeChange() {
    if (this.alreadyUsedCodes.includes(this.item.code)) {
      this.addAlreadyExistCodeError();
    } else {
      this.manualValidator.removeError("code");
    }
  }

  private addAlreadyExistCodeError() {
    this.manualValidator.addError("code", this.translate("codeMustBeUnique"));
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `systemComponents.${code}`, size);
  }
}
